<template>
  <Teleport to="body">
    <div v-if="isModeSwitcherShowable" class="mode-switcher" id="mode-switcher" :class="{
      'mode-switcher--helper': mode === 'helper',
      'mode-switcher--user': mode === 'user'
    }" @click="handleClick" @keydown.enter="handleClick" @keydown.space="handleClick" role="button" tabindex="0"
      :aria-label="$t('components.mode_switcher.switch_to', { mode: $t(`components.mode_switcher.modes.${mode === 'user' ? 'helper' : 'user'}`) })">
      <span class="mode-text senior">{{ $t('components.mode_switcher.modes.user') }}</span>
      <span class="mode-text helper">{{ $t('components.mode_switcher.modes.helper') }}</span>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useModeStore } from '~/stores/mode';
import { useAuthStore } from '~/stores/authStore';
import { ref, onMounted, computed, nextTick } from 'vue';
import { useLogger } from '~/composables/useLogger';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const logger = useLogger();
const modeStore = useModeStore();
const authStore = useAuthStore();
const router = useRouter();
const { mode } = storeToRefs(modeStore);
const { t } = useI18n();

// Simple reactive state
const isNavigating = ref(false);

// Determine if the mode switcher should be shown
const isModeSwitcherShowable = computed(() => {
  return !authStore.isLockedToUserDevice && authStore.userLoggedIn;
});

const handleClick = async (event: Event) => {
  logger.debug('ModeSwitcher: handleClick', { currentMode: mode.value });

  try {
    // Determine the next mode based on current mode
    const nextMode = mode.value === 'user' ? 'helper' : 'user';
    
    // Mark as navigating
    isNavigating.value = true;

    // Handle navigation between modes
    if (nextMode === 'helper') {
      // User is navigating from user to helper mode
      logger.debug('ModeSwitcher: Setting latest area to Helper');
      authStore.setLatestArea('Helper');
      
      logger.debug('ModeSwitcher: Navigating to helper mode (/)');
      await router.push('/');
    } else {
      // User is navigating from helper to user mode
      logger.debug('ModeSwitcher: Setting latest area to User');
      authStore.setLatestArea('User');
      
      logger.debug('ModeSwitcher: Navigating to user mode (/user/home)');
      await router.push('/user/home');
    }
  } catch (error) {
    logger.error('ModeSwitcher: Error in click handler', { error });
  } finally {
    isNavigating.value = false;
  }
};

onMounted(async () => {
  logger.debug('ModeSwitcher: mounted', { currentMode: mode.value });
  
  // Wait for initial render to complete
  await nextTick();
});
</script>

<style scoped>
.mode-switcher {
  position: fixed;
  right: 16px;
  top: 10px;
  z-index: 999999; /* Super high z-index to ensure it's above everything */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem; /* Changed from 9999px to match BackToHome button's rounded-lg/rounded-xl */
  background-color: #007500; /* Exact same green used for backbutton-green */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); /* Enhanced shadow for visual prominence */
  transition: background-color 0.3s ease;
  overflow: hidden;
  height: 40px;
  min-width: 150px;
  padding: 0;
  border: 1px solid white;
  isolation: isolate; /* Create new stacking context */
  transform: translateZ(0); /* Force hardware acceleration */
  pointer-events: auto !important; /* Ensure clicks are captured */
  
  /* Responsive styles for larger screens */
  @media (min-width: 640px) {
    height: 52px;
    min-width: 180px;
    padding: 0;
    border: 2px solid white;
  }
}

.mode-text {
  color: white;
  font-weight: 500;
  font-size: 14px; /* Smaller font for mobile */
  line-height: 24px; /* Line height for mobile */
  padding: 0 8px; /* Horizontal padding */
  opacity: 0.7;
  
  /* Responsive styles for larger screens */
  @media (min-width: 640px) {
    font-size: 16px;
    line-height: 28px;
    padding: 0 10px; /* Horizontal padding */
  }
}

.senior {
  border-radius: 0.5rem; /* Reduced corner radius for inner elements */
  margin-right: 3px; /* Small gap between options */
}

.helper {
  border-radius: 0.5rem; /* Reduced corner radius for inner elements */
  margin-left: 3px; /* Small gap between options */
}

/* Show the correct highlighted state based on current mode */
.mode-switcher--user .senior {
  background-color: #00A000; /* Updated to match backbutton-green-hover */
  opacity: 1;
  margin-left: 0; /* No left margin - touch left edge */
  border-radius: 0.5rem; /* Match other elements */
  padding: 4px 8px 4px 12px; /* More padding on left side */
}

.mode-switcher--helper .helper {
  background-color: #00A000; /* Updated to match backbutton-green-hover */
  opacity: 1;
  margin-right: 0; /* No right margin - touch right edge */
  border-radius: 0.5rem; /* Match other elements */
  padding: 4px 12px 4px 8px; /* More padding on right side */
}

/* Handle safe areas */
@supports (padding: max(0px)) {
  .mode-switcher {
    right: max(16px, calc(env(safe-area-inset-right) + 16px));
    top: max(10px, calc(env(safe-area-inset-top) + 10px)); /* Updated to match new top value */
  }
}

.mode-switcher--user .helper {
  background-color: transparent;
}

.mode-switcher--helper .senior {
  background-color: transparent;
}

/* Mobile specific adjustments */
@media (max-width: 639px) {
  .mode-switcher--user .senior {
    padding: 3px 6px 3px 8px; /* Smaller padding for mobile */
  }
  
  .mode-switcher--helper .helper {
    padding: 3px 8px 3px 6px; /* Smaller padding for mobile */
  }
}
</style>
